import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { of } from 'rxjs';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { RxUtils, Tr, TypeUtils } from '@utils';
import { carModelService, CreateStockDto, FuelType, Stock, stockService } from '@services';
import { useService } from '@hooks';
import { Autocomplete, Grid, MenuItem, Stack } from '@mui/material';
import { FormikNumber, FormikRadio, FormikTextfield, ImageUpload } from '@components/common';
import { FormStepGreySection, FormStepLayout, FormStepQuestion } from '@components/portal/common';

const DEFAULT_VERSION_TEXT = 'Standard';
type FormFields = Extendable<{
	brand: string;
	model: string;
	version?: string;
	b2cBrand?: string;
	b2cModel?: string;
	b2cVersion?: string;
	b2cSegment?: string;
	fuelType: FuelType;
	title?: string;
	message?: string;
	externalLink?: string;
	fullPrice: number;
	leasingPrice: number;
	insurance?: number;
	vehicleTax?: number;
	mAndR?: number;
	tires?: number;
	residiualValue?: number;
	isAvailable: string;
	stockAmount?: number;
	buyToOrder?: number;
}>;

export interface IAddEditStockFormProps {
	values?: Partial<Stock>;
	onSubmit?: (values: Partial<CreateStockDto>, images?: File[]) => void;
}

export const AddEditStockForm: FC<IAddEditStockFormProps> = ({ values, onSubmit }) => {
	const { t } = useTranslation('portal');
	const [imagesChanged, setImagesChanged] = useState(false);
	const [currentImages, setCurrentImages] = useState<string[]>([]);
	const [newImages, setNewImages] = useState<File[]>([]);

	useEffect(() => {
		values?.id && RxUtils.promisify(stockService.getImages(values as Stock), setCurrentImages);
	}, [values]);

	const formik = useFormik<FormFields>({
		initialValues: {
			title: '',
			message: '',
			externalLink: '',
			fullPrice: '',
			leasingPrice: '',
			insurance: '',
			vehicleTax: '',
			mAndR: '',
			tires: '',
			residiualValue: '',
			stockAmount: '',
			buyToOrder: '',
			b2cBrand: '',
			b2cModel: '',
			b2cVersion: '',
			b2cSegment: '',
			...values,
			brand: values?.carModel?.brand || '',
			model: values?.carModel?.model || '',
			version: values?.carModel?.version || '',
			fuelType: values?.carModel?.motorFuelType || '',
			isAvailable: TypeUtils.isDefined(values?.isAvailable) ? (values?.isAvailable ? '1' : '0') : ''
		},
		enableReinitialize: true,
		validateOnChange: true,
		validateOnMount: true,
		validationSchema: yup.object({
			brand: yup.string().required(t('field-required')),
			model: yup.string().required(t('field-required')),
			fuelType: yup.string().required(t('field-required')),
			b2cBrand: yup.string().optional().nullable(),
			b2cModel: yup.string().optional().nullable(),
			b2cVersion: yup.string().optional().nullable(),
			b2cSegment: yup.string().optional().nullable(),
			title: yup.string().optional().nullable(),
			message: yup.string().optional().nullable(),
			externalLink: yup.string().url(t('url-invalid')),
			fullPrice: yup.number().required(t('field-required')),
			leasingPrice: yup.number().required(t('field-required')),
			insurance: yup.number().required(t('field-required')),
			vehicleTax: yup.number().required(t('field-required')),
			mAndR: yup.number().required(t('field-required')),
			tires: yup.number().required(t('field-required')),
			residiualValue: yup.number().required(t('field-required')),
			buyToOrder: yup.number().required(t('field-required')),
			stockAmount: yup.number().when('isAvailable', {
				is: '1',
				then: yup.number().required(t('field-required')),
				otherwise: yup.number().nullable()
			})
		}),
		onSubmit: (
			{
				model,
				brand,
				version,
				b2cBrand,
				b2cModel,
				b2cVersion,
				b2cSegment,
				fuelType,
				title,
				message,
				externalLink,
				fullPrice,
				leasingPrice,
				insurance,
				vehicleTax,
				mAndR,
				tires,
				residiualValue,
				isAvailable,
				stockAmount,
				buyToOrder
			},
			api
		) => {
			onSubmit?.(
				{
					model,
					brand,
					version: version !== DEFAULT_VERSION_TEXT ? version : null,
					b2cBrand,
					b2cModel,
					b2cVersion,
					b2cSegment,
					fuelType,
					title,
					message,
					externalLink,
					fullPrice,
					leasingPrice,
					insurance,
					vehicleTax,
					mAndR,
					tires,
					residiualValue,
					buyToOrder,
					isAvailable: isAvailable === '1',
					stockAmount: isAvailable === '1' ? +stockAmount : undefined
				},
				newImages
			);
			api.setSubmitting(false);
		}
	});
	const { submitForm, handleSubmit } = formik;
	const disabled = imagesChanged ? false : !formik.isValid || formik.isSubmitting;
	const [fuelTypes, setFuelTypes] = useState(Object.values(FuelType));
	const { data: brands = [] } = useService(() => carModelService.getAllBrands(), []);
	const { data: models = [] } = useService(
		() => (formik.values.brand ? carModelService.getAllModels(formik.values.brand) : of([])),
		[formik.values.brand]
	);
	const { data: versions = [] } = useService(
		() =>
			formik.values.brand && formik.values.model ? carModelService.getAllVersions(formik.values.brand, formik.values.model) : of([]),
		[formik.values.model]
	);

	useEffect(() => {
		let displayFuelTypes = Object.values(FuelType);

		if (formik.values.version) {
			const searchingVersion = formik.values.version === DEFAULT_VERSION_TEXT ? null : formik.values.version;
			const model = versions.find(({ version }) => version === searchingVersion);
			model && (displayFuelTypes = [model.motorFuelType]);
		} else if (versions.length) {
			displayFuelTypes = versions.map(({ motorFuelType }) => motorFuelType);
		} else if (models.length) {
			displayFuelTypes = models.map(({ motorFuelType }) => motorFuelType);
		}

		setFuelTypes([...new Set(displayFuelTypes)]);
	}, [models, versions, formik.values.version]);

	return (
		<form onSubmit={handleSubmit} style={{ flexGrow: 1, overflowY: 'hidden' }}>
			<FormStepLayout
				primaryButtonProps={{
					disabled,
					children: <Tr.Portal path={values ? 'stock.add-edit-stock.update-stock' : 'stock.add-edit-stock.add-stock'} />,
					onClick: submitForm,
					'data-testid': 'add-edit-stock-next-button'
				}}
				hideSecondaryButton
			>
				<Stack direction="column" spacing={8}>
					<FormStepQuestion title={<Tr.Portal path="stock.add-edit-stock.vehicle-details" />} titleProps={{ variant: 'h4' }}>
						<Grid container rowSpacing={5} columnSpacing={4}>
							<Grid item xs={6}>
								<Autocomplete
									isOptionEqualToValue={(option, value) => option.brand === value.brand}
									getOptionLabel={(option) => (typeof option === 'string' ? option : option.brand)}
									options={brands}
									freeSolo
									fullWidth
									onChange={(e, value) => {
										formik.setFieldValue('brand', (typeof value === 'string' ? value : value?.brand) || '');
										formik.setFieldValue('model', '');
										formik.setFieldValue('version', '');
										formik.setFieldValue('fuelType', '');
									}}
									onInputChange={(_e, _value, reason) => {
										if (reason === 'clear') {
											formik.setFieldValue('brand', '');
											formik.setFieldValue('model', '');
											formik.setFieldValue('version', '');
											formik.setFieldValue('fuelType', '');
										}
									}}
									inputValue={formik.values.brand || ''}
									renderInput={(params) => (
										<FormikTextfield
											{...params}
											formikApi={formik}
											name="brand"
											title={t('stock.add-edit-stock.brand')}
											placeholder={t('stock.add-edit-stock.brand-placeholder')}
											required
											InputProps={{
												...params.InputProps,
												endAdornment: params.InputProps.endAdornment
											}}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="b2cBrand"
									title={t('stock.add-edit-stock.brand-alias')}
									placeholder={t('stock.add-edit-stock.brand-alias-placeholder')}
								/>
							</Grid>

							<Grid item xs={6}>
								<Autocomplete
									isOptionEqualToValue={(option, value) => option.model === value.model}
									getOptionLabel={(option) => (typeof option === 'string' ? option : option.model)}
									options={models}
									freeSolo
									fullWidth
									onChange={(e, value) => {
										formik.setFieldValue('model', typeof value === 'string' ? value : value?.model || '');
										formik.setFieldValue('version', '');
										formik.setFieldValue('fuelType', '');
									}}
									onInputChange={(_e, _value, reason) => {
										if (reason === 'clear') {
											formik.setFieldValue('model', '');
											formik.setFieldValue('version', '');
											formik.setFieldValue('fuelType', '');
										}
									}}
									inputValue={formik.values.model || ''}
									renderInput={(params) => (
										<FormikTextfield
											{...params}
											formikApi={formik}
											name="model"
											title={t('stock.add-edit-stock.model')}
											placeholder={t('stock.add-edit-stock.model-placeholder')}
											disabled={!formik.values.brand}
											required
											InputProps={{
												...params.InputProps,
												endAdornment: params.InputProps.endAdornment
											}}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="b2cModel"
									title={t('stock.add-edit-stock.model-alias')}
									placeholder={t('stock.add-edit-stock.model-alias-placeholder')}
								/>
							</Grid>

							<Grid item xs={6}>
								<Autocomplete
									isOptionEqualToValue={(option, value) => option.version === value.version}
									getOptionLabel={(option) =>
										(typeof option === 'string' ? option : option.version) || DEFAULT_VERSION_TEXT
									}
									options={versions}
									freeSolo
									fullWidth
									onChange={(e, value) =>
										formik.setFieldValue(
											'version',
											(typeof value === 'string' ? value : value?.version) || DEFAULT_VERSION_TEXT
										)
									}
									onInputChange={(_e, _value, reason) => {
										reason === 'clear' && formik.setFieldValue('version', '');
									}}
									inputValue={formik.values.version || ''}
									renderInput={(params) => (
										<FormikTextfield
											{...params}
											formikApi={formik}
											name="version"
											title={t('stock.add-edit-stock.version')}
											placeholder={t('stock.add-edit-stock.version-placeholder')}
											disabled={!formik.values.model || versions?.length <= 0}
											InputProps={{
												...params.InputProps,
												endAdornment: params.InputProps.endAdornment
											}}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="b2cVersion"
									title={t('stock.add-edit-stock.version-alias')}
									placeholder={t('stock.add-edit-stock.version-alias-placeholder')}
								/>
							</Grid>

							<Grid item xs={6}>
								<FormikTextfield
									select
									formikApi={formik}
									name="fuelType"
									title={t('stock.add-edit-stock.fuel-type')}
									placeholder={t('stock.add-edit-stock.fuel-type-placeholder')}
									required
								>
									{fuelTypes.map((type) => (
										<MenuItem key={`fuel-type-${type}`} value={type}>
											{type}
										</MenuItem>
									))}
								</FormikTextfield>
							</Grid>
							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="b2cSegment"
									title={t('stock.add-edit-stock.segment-alias')}
									placeholder={t('stock.add-edit-stock.segment-alias-placeholder')}
								/>
							</Grid>

							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="title"
									title={t('stock.add-edit-stock.title')}
									placeholder={t('stock.add-edit-stock.title-placeholder')}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="message"
									title={t('stock.add-edit-stock.message')}
									placeholder={t('stock.add-edit-stock.message-placeholder')}
								/>
							</Grid>

							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="externalLink"
									title={t('stock.add-edit-stock.external-link')}
									placeholder={t('stock.add-edit-stock.external-link-placeholder')}
								/>
							</Grid>
							<Grid item xs={6}>
								<ImageUpload
									title={<Tr.Portal path="stock.images" />}
									multiple
									images={currentImages}
									onChange={(files) => (setNewImages(files), setImagesChanged(true))}
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>
					<FormStepQuestion title={<Tr.Portal path="stock.add-edit-stock.payment-details" />} titleProps={{ variant: 'h4' }}>
						<Grid container rowSpacing={5} columnSpacing={4}>
							<Grid item xs={6}>
								<FormikNumber
									formikApi={formik}
									name="fullPrice"
									title={t('stock.add-edit-stock.full-price')}
									placeholder={t('stock.add-edit-stock.full-price-placeholder')}
									required
									decimal={0}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikNumber
									formikApi={formik}
									name="leasingPrice"
									title={t('stock.add-edit-stock.leasing-price')}
									placeholder={t('stock.add-edit-stock.leasing-price-placeholder')}
									required
									decimal={0}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikNumber
									formikApi={formik}
									name="insurance"
									title={t('stock.add-edit-stock.insurance-price')}
									placeholder={t('stock.add-edit-stock.insurance-price-placeholder')}
									required
									decimal={0}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikNumber
									formikApi={formik}
									name="vehicleTax"
									title={t('stock.add-edit-stock.vehicle-tax-price')}
									placeholder={t('stock.add-edit-stock.vehicle-tax-price-placeholder')}
									required
									decimal={0}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikNumber
									formikApi={formik}
									name="mAndR"
									title={t('stock.add-edit-stock.mAndR-price')}
									placeholder={t('stock.add-edit-stock.mAndR-price-placeholder')}
									required
									decimal={2}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikNumber
									formikApi={formik}
									name="tires"
									title={t('stock.add-edit-stock.tires-price')}
									placeholder={t('stock.add-edit-stock.tires-price-placeholder')}
									required
									decimal={2}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikNumber
									formikApi={formik}
									name="residiualValue"
									title={t('stock.add-edit-stock.residiual-value')}
									placeholder={t('stock.add-edit-stock.residiual-value-placeholder')}
									required
									decimal={2}
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>
					<Stack direction="column" spacing={2}>
						<FormStepQuestion
							title={<Tr.Portal path="stock.add-edit-stock.availability-details" />}
							titleProps={{ variant: 'h4' }}
						>
							<Grid container rowSpacing={5} columnSpacing={4}>
								<Grid item xs={6}>
									<FormikRadio
										formikApi={formik}
										name="isAvailable"
										value="1"
										formControlLabelProps={{ label: <Tr.Portal path="yes" /> }}
									/>
								</Grid>
								<Grid item xs={6}>
									<FormikRadio
										formikApi={formik}
										name="isAvailable"
										value="0"
										formControlLabelProps={{ label: <Tr.Portal path="no" /> }}
									/>
								</Grid>
							</Grid>
						</FormStepQuestion>
						{formik.values.isAvailable ? (
							<FormStepGreySection>
								<Grid container rowSpacing={5} columnSpacing={4}>
									{formik.values.isAvailable === '1' ? (
										<Grid item xs={6}>
											<FormikNumber
												formikApi={formik}
												required
												name="stockAmount"
												title={t('stock.add-edit-stock.amount')}
												placeholder={t('stock.add-edit-stock.amount')}
												decimal={0}
											/>
										</Grid>
									) : null}
									<Grid item xs={6}>
										<FormikNumber
											formikApi={formik}
											name="buyToOrder"
											required
											title={t('stock.add-edit-stock.delivery-time')}
											placeholder={t('stock.add-edit-stock.delivery-time')}
											decimal={0}
										/>
									</Grid>
								</Grid>
							</FormStepGreySection>
						) : null}
					</Stack>
				</Stack>
			</FormStepLayout>
		</form>
	);
};
